/**
 * Video Editor — shared surface parts (Phase B3).
 */
import React from 'react';
import type { VideoProject, VideoJob } from '../types';

export function formatDuration(sec?: number | null): string {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatBytes(n?: number | null): string {
  if (!n || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Surface header — eyebrow ("Surface N of 7") + title + optional subtitle.
export const SurfaceHeader: React.FC<{
  eyebrow: string; title: string; subtitle?: string;
}> = ({ eyebrow, title, subtitle }) => (
  <header className="ve-surface-header">
    <div className="ve-surface-header__eyebrow">{eyebrow}</div>
    <h1 className="ve-surface-header__title">{title}</h1>
    {subtitle && <p className="ve-surface-header__subtitle">{subtitle}</p>}
  </header>
);

// JobProgress — the building / rendering live view. Coarse progress off the
// VideoJob state (the pipeline is a blocking subprocess; fine-grained
// streaming is pipeline STUB-2). Never a spinner-only state — the stages and
// attempt count are always shown.
export const JobProgress: React.FC<{
  label: string;
  phase: 'building' | 'rendering';
  job: VideoJob | null;
  project: VideoProject;
  onStart: () => void;
  onContinue?: () => void;
}> = ({ label, phase, job, project, onStart, onContinue }) => {
  const running = job && (job.status === 'queued' || job.status === 'running');
  const stages = (job && job.output && (job.output as any).stagesCompleted) || [];
  const progress = job && job.progress;
  const building = phase === 'building';

  return (
    <div>
      <SurfaceHeader
        eyebrow={building ? 'Step 2 of 3' : 'Step 3 of 3'}
        title={building ? 'Building your video' : 'Rendering your cut'}
        subtitle={running
          ? (building
              ? 'Analysing the footage and cutting a first pass — this can take a few minutes.'
              : 'Rendering on the VM — this can take a few minutes.')
          : 'Run the pipeline stage, or continue if it has already finished.'}
      />

      {running ? (
        <div className="ve-panel ve-job">
          {progress ? (
            <>
              <div className="ve-job__bar ve-job__bar--determinate">
                <span style={{ width: `${Math.min(100, Math.max(0, progress.percent || 0))}%` }} />
              </div>
              <div className="ve-job__meta">
                <strong>{progress.label || 'Working…'}</strong>
                {typeof progress.percent === 'number' &&
                  <span className="ve-muted"> · {progress.percent}%</span>}
                {progress.elapsedS ? <span className="ve-muted"> · {progress.elapsedS}s</span> : null}
              </div>
            </>
          ) : (
            <>
              <div className="ve-job__bar"><span /></div>
              <div className="ve-job__meta">
                <strong>{job!.status === 'queued' ? 'Queued' : 'Running'}</strong>
                {typeof job!.queuePosition === 'number' && job!.queuePosition > 0 &&
                  <span className="ve-muted"> · {job!.queuePosition} ahead in the queue</span>}
                {(job!.attempts || 0) > 1 && <span className="ve-muted"> · attempt {job!.attempts}</span>}
              </div>
            </>
          )}
          {stages.length > 0 && (
            <div className="ve-job__stages">
              {['analyse', 'plan', 'render'].map(s => (
                <span key={s} className={`ve-stage${stages.includes(s) ? ' ve-stage--done' : ''}`}>
                  {stages.includes(s) ? '✓' : '○'} {s}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="ve-panel ve-job">
          <p className="ve-muted" style={{ marginTop: 0 }}>
            No pipeline stage is running right now.
          </p>
          <div className="ve-actions">
            <button className="ve-btn ve-btn--primary" onClick={onStart}>{label}</button>
            {onContinue && (
              <button className="ve-btn" onClick={onContinue}>Continue →</button>
            )}
          </div>
        </div>
      )}

      {project.renderOutput?.durationSec ? (
        <p className="ve-muted">
          Last render: {formatDuration(project.renderOutput.durationSec)} ·{' '}
          {formatBytes(project.renderOutput.sizeBytes)}
        </p>
      ) : null}
    </div>
  );
};
